/**
 * The dashboard's legal row — company line, legal links, cookie settings,
 * sign out (COL-533).
 *
 * Extracted because BOTH dashboards need it and for a while neither had it.
 * `useHideSiteFooter()` suppresses the site-wide marketing footer under
 * /dashboard, and V1 declared that suppression while carrying no footer of
 * its own — so every tenant on the old dashboard, feature flag or not, lost
 * Impressum, Datenschutz, AGB and Cookie settings outright. That is the
 * shipped dashboard on production.
 *
 * Cookie settings is the one that cannot simply be dropped: it is the only
 * control that reopens the consent dialog after a choice has been made, so
 * without it a tenant has no way to withdraw consent. `Footer.tsx` carries
 * it site-wide; this row carries it here.
 *
 * Rendered standalone by V1 and as the bottom band of V2's own footer, so
 * the two cannot drift apart again.
 */

import { Link } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { openCookieSettings } from "@/lib/consent";
import { translateDictionary } from "@/lib/i18n/dictionary";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { getTenantDashboardV2Labels } from "@/lib/tenant-dashboard-v2-copy";

type Props = {
  /**
   * V1 renders this row on its own, so it needs the dark band and the top
   * rule. V2 nests it under its CTA, which already draws both.
   */
  standalone?: boolean;
};

export default function TenantLegalFooter({ standalone = false }: Props) {
  const { tDict, language: activeLanguage } = useLanguage();
  const language = baseLanguageFor(activeLanguage);
  const d = translateDictionary(
    "tenant.dashboardV2",
    getTenantDashboardV2Labels(language),
    (key, literal) => tDict(key, literal ?? "", activeLanguage)
  );
  const { signOut } = useAuth();

  const linkClass =
    "text-sm text-background/70 underline-offset-4 transition-colors hover:text-background hover:underline";

  const row = (
    <div
      className={
        standalone
          ? "flex flex-col gap-4 text-background/70 sm:flex-row sm:items-center sm:justify-between"
          : "mt-8 flex flex-col gap-4 border-t border-background/15 pt-6 text-background/70 sm:flex-row sm:items-center sm:justify-between"
      }
    >
      <p className="text-sm">{d.footerCompany}</p>
      <nav className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Link className={linkClass} to="/privacy-policy">
          {d.footerPrivacy}
        </Link>
        <Link className={linkClass} to="/imprint">
          {d.footerImprint}
        </Link>
        <Link className={linkClass} to="/terms-of-service">
          {d.footerTerms}
        </Link>
        {/*
          A button, not a link: this reopens the consent dialog in place via
          a window event, so there is no route to navigate to.
        */}
        <button
          type="button"
          className={linkClass}
          onClick={openCookieSettings}
        >
          {d.footerCookieSettings}
        </button>
        {/*
          A button, not a link: signing out is an action with a side effect,
          and the design puts it in this row. `signOut` already handles the
          redirect, so there is nothing to navigate to here.
        */}
        <button
          type="button"
          className={linkClass}
          onClick={() => void signOut()}
        >
          {d.footerLogout}
        </button>
      </nav>
    </div>
  );

  if (!standalone) return row;

  return (
    <footer className="mt-12 w-full bg-foreground py-8 text-background">
      <div className="mx-auto w-full max-w-screen-2xl px-6 lg:px-14">{row}</div>
    </footer>
  );
}
